import React, { useState, useMemo, useEffect } from "react";
import { observer } from "mobx-react-lite";

import { SectionMenuItemsModel } from "./SectionMenuItems";
import {
  ViewSewerObject,
  ViewSewerObjectFromAssignedProject,
} from "../../../services/authorization/Permissions";
import SewerObjectsMap from "../../../components/map/SewerObjectsMap";
import PageLayout from "../../_layout/PageLayout";
import { ActionsSection } from "../../../components/data-components/sections/DataSections";
import { SewerObjectsMapStorageModel } from "../../../services/sewer-object/SewerObjectsMapStorage";
import { api } from "../../../services/api/ApiService";
import useUrlParams from "../../../services/url/UrlParamsService";
import { CurrentSectionStorageModel } from "../../../services/sewer-object/CurrentSectionStorage";
import { useHistory } from "react-router-dom";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";
import { EntityTypeFromPath } from "../../../services/breadcrumbs/EntityTypeFromPathModel";
import useRouteService from "../../../services/route-service/RouteService";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { isUserOnProjects } = useRouteService();

  const [currentSectionStorage] = useState(() =>
    CurrentSectionStorageModel.create(
      {
        currentSectionId: urlParamsService.getSectionId()!,
        parentEntityFromPath: {
          id: urlParamsService.getSewerObjectParentId()!,
          type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job,
        },
      },
      { api }
    )
  );

  const [sewerObjectsMapStorage] = useState(() =>
    SewerObjectsMapStorageModel.create(
      { inputSewerObjects: [currentSectionStorage.currentSectionId], autoLoad: false },
      { api }
    )
  );

  const menuItems = useMemo(() => {
    const sectionMenuItems = SectionMenuItemsModel.create({
      sectionId: currentSectionStorage.currentSectionId,
      section: currentSectionStorage.currentSection,
      parentIdFromPath: currentSectionStorage.parentEntityFromPath.id,
    });
    return {
      staticItems: sectionMenuItems.getStaticMenuItems(),
      dynamicItems: sectionMenuItems.getDynamicMenuItems(),
    };
  }, [
    currentSectionStorage.parentEntityFromPath,
    currentSectionStorage.currentSection,
    currentSectionStorage.currentSectionId,
  ]);

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([
        {
          entity: currentSectionStorage.parentEntityFromPath.type,
          name: await currentSectionStorage.getParentEntityName(
            currentSectionStorage.currentSection.project.name
          ),
        },
        {
          entity: EntityTypeFromPath.SewerObject,
          name: currentSectionStorage.currentSection.name,
        },
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [
    currentSectionStorage,
    currentSectionStorage.currentSection.name,
    currentSectionStorage.currentSection.project.name,
    currentSectionStorage.parentEntityFromPath.type,
    history.location.pathname,
  ]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewSewerObject, ViewSewerObjectFromAssignedProject]}
      titleKey="ViewSectionOnMap.title"
      titleContext={{ sectionName: currentSectionStorage.currentSection.name }}
      id="view-section-on-map"
      menuItems={menuItems}
      isLoading={currentSectionStorage.isLoading || sewerObjectsMapStorage.isDataLoading}
    >
      {currentSectionStorage.currentSectionId &&
        !currentSectionStorage.currentSection.hasGeometry && (
          <ActionsSection noBorder warningKey="ViewSectionOnMap.noGeometryWarning" />
        )}
      {currentSectionStorage.currentSectionId &&
        currentSectionStorage.currentSection.hasGeometry && (
          <div className="map-page-container">
            <SewerObjectsMap sewerObjectsMapStorage={sewerObjectsMapStorage} fullPage />
          </div>
        )}
    </PageLayout>
  );
});
